import { NoData } from "@/components/common/section-components/NoData";
import { GrossMarginRequestActions } from "@/components/common/section-components/action/GrossMarginRequestActions";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { GrossMarginRequestFilter } from "@/components/common/section-components/filters/GrossMarginRequestFilter";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { GrossMarginRequestStatus } from "@/constants/entity-status";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { Flex } from "@chakra-ui/react";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  clearGrossMarginFiltersHandler,
  fetchGrossMarginRequestsHandler,
  submitGrossMarginFiltersHandler,
} from "./schema/handler-functions";
import { GrossMarginRequestListItem, GrossMarginRequestsListResponse } from "./schema/gross-margin-requests.schema";
import { grossMarginRequestsHeaderElements } from "./schema/header-elements";

export const GrossMarginRequestsPage = () => {
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState<boolean>(false);
  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [list, setList] = useState<GrossMarginRequestsListResponse>();
  const [currentFilters, setCurrentFilters] = useState<any>();
  const [page, setPage] = useState<number>(1);

  const { control, handleSubmit, setValue } = useForm<FieldValues>();
  const values = useWatch({ control });
  const filtersEmpty = useMemo(
    () => Object.values(values ?? {}).every((v) => isEmpty(v)),
    [values],
  );

  useEffect(() => {
    setIsActive(true);
  }, []);

  const fetchList = async (filters?: any, showLoad = true) => {
    await fetchGrossMarginRequestsHandler({
      filters,
      showLoad,
      setLoading,
      setList,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    if (isActive) fetchList({ "P.Pagina": 1 });
  }, [isActive]);

  const submitFilters = async (event: FieldValues) => {
    await submitGrossMarginFiltersHandler({
      event,
      setPage,
      fetchList,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      filtersEmpty,
    });
  };

  const clearFilters = async () => {
    await clearGrossMarginFiltersHandler({
      setValue,
      setFiltersApplied,
      setOpenFilters,
      setPage,
      setCurrentFilters,
      fetchList,
    });
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchList({ ...currentFilters, "P.Pagina": nextPage });
  };

  const handleDetail = (id: number) => {
    navigate(AppRoutes.GROSS_MARGIN_REQUESTS_DETAIL.replace(":id", String(id)));
  };

  const getActionElements = (item: GrossMarginRequestListItem) => (
    <GrossMarginRequestActions handleDetail={handleDetail} item={item} />
  );

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Margen Bruto Personalizado"
        loading={loading}
        showButtons={false}
      />

      <GrossMarginRequestFilter
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        clearFilters={clearFilters}
        control={control}
        handleSubmit={handleSubmit}
        filtersApplied={filtersApplied}
        disableFilters={loading}
        loadingFilters={loading}
        disableApply={filtersEmpty && !filtersApplied}
      />

      <Flex flexDir="column">
        {Boolean(!loading && !list?.listado?.length) && <NoData />}

        {Boolean(!loading && list?.listado?.length) && (
          <>
            <StandardTable
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              noSelection
              items={
                list?.listado?.map((el: GrossMarginRequestListItem) => ({
                  ...el,
                  displayFecha: el?.fecha ? dayjs(el.fecha).format("DD/MM/YYYY") : "-",
                  displayCultivos: el?.cultivos?.join(", ") || "-",
                  displayEstado: (
                    <RequestStatusBadge
                      status={el?.estado as GrossMarginRequestStatus}
                    />
                  ),
                })) ?? []
              }
              headers={grossMarginRequestsHeaderElements}
              actionElements={getActionElements}
            />

            {Number(list?.totalPages) > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={list?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
